import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { animeList, articles, animeCategories } from '@/mocks/animeData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const anime = animeList.find((a) => a.id === id);
  const article = articles.find((a) => a.id === id);

  if (!anime && !article) {
    return (
      <Layout>
        <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
              <i className="ri-error-warning-line text-4xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">内容不存在</h1>
            <p className="text-foreground-500 mb-6">您访问的动漫或文章可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  if (anime) {
    const catName = animeCategories.find((c) => c.slug === anime.category)?.name || anime.category;
    const relatedAnime = animeList.filter((a) => a.id !== anime.id && a.category === anime.category).slice(0, 4);

    const animeSchema = {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: anime.title,
      description: anime.description,
      url: `https://beyondtoon.github.io/article/${anime.id}`,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: anime.rating,
        bestRating: '10',
        ratingCount: Math.floor(Math.random() * 50000) + 10000,
      },
    };

    return (
      <Layout>
        <SEOHead
          title={`${anime.title}在线观看 - 91动漫`}
          description={`${anime.title}：${anime.description.slice(0, 120)}。${anime.region}${catName}，${anime.episodes}，评分${anime.rating}，高清免费在线播放。`}
          keywords={`${anime.title},${anime.title}在线观看,${anime.tags.join(',')},91动漫`}
          canonicalPath={`/article/${anime.id}`}
          ogType="article"
          schema={animeSchema}
        />

        <article className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${anime.category}` },
                { label: anime.title },
              ]}
            />

            <div className="mt-6">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-40 md:w-52 mx-auto md:mx-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={anime.cover}
                      alt={anime.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                    {anime.title}
                  </h1>

                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold text-primary-500">{anime.rating}</span>
                      <span className="text-xs text-foreground-400">分</span>
                    </div>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600 whitespace-nowrap">{anime.region}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600 whitespace-nowrap">{anime.year}</span>
                    <span className="text-foreground-300">|</span>
                    <span className={`text-sm font-medium whitespace-nowrap ${anime.status === '连载中' ? 'text-accent-600' : 'text-foreground-500'}`}>
                      {anime.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {anime.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-500 mt-4 leading-relaxed">
                    {anime.description}
                  </p>

                  <div className="flex items-center gap-3 mt-5">
                    <button className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      立即播放
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">剧集列表</h2>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {Array.from({ length: 24 }, (_, i) => i + 1).map((ep) => (
                    <button
                      key={ep}
                      className="px-3 py-2 rounded-md bg-background-100 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      第{ep}集
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {relatedAnime.length > 0 && (
              <div className="mt-14">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {relatedAnime.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                          {item.rating}
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </Layout>
    );
  }

  if (article) {
    const catName = animeCategories.find((c) => c.slug === article.category)?.name || article.category;
    const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishDate,
      image: article.cover,
      url: `https://beyondtoon.github.io/article/${article.id}`,
      publisher: {
        '@type': 'Organization',
        name: '91动漫',
      },
    };

    return (
      <Layout>
        <SEOHead
          title={`${article.title} - 91动漫资讯`}
          description={article.summary}
          keywords={`${article.tags.join(',')},91动漫,动漫资讯`}
          canonicalPath={`/article/${article.id}`}
          ogType="article"
          schema={articleSchema}
        />

        <article className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '动漫资讯' },
                { label: article.title },
              ]}
            />

            <div className="mt-6">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center text-xs font-bold">
                    {article.author[0]}
                  </div>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">{article.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.publishDate}</span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount.toLocaleString()} 阅读
                </span>
                <div className="flex items-center gap-1.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg overflow-hidden">
                <img
                  src={article.cover}
                  alt={article.title}
                  className="w-full aspect-[2/1] object-cover object-top"
                />
              </div>

              <div
                className="mt-8 prose prose-sm max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:text-base [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200 flex items-center gap-4">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i>
                  赞
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-star-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span className="whitespace-nowrap">{item.publishDate}</span>
                          <span className="whitespace-nowrap">{item.readCount.toLocaleString()} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </Layout>
    );
  }

  return null;
}